import { useMutation } from '@apollo/client';
import * as React from 'react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import Button from '../../core/components/Button.tsx';
import Modal from '../../core/components/Modal.tsx';
import { useToaster } from '../../core/components/Toast.tsx';
import { useApplicationContext } from '../../core/contexts/ApplicationContext.tsx';
import { isValidKnowledgeUrl } from '../../products/assistants/components/config/ConfigSteps.tsx';
import { canConfigureKnowledgeSources } from '../../users/utils.ts';
import { graphqlErrorMessage } from '../errors.ts';
import { CREATE_PROJECT, SHARE_PROJECT, UPLOAD_PROJECT_KNOWLEDGE_FILE } from '../queries.ts';
import {
    PROJECT_FORM_DEFAULTS,
    ProjectDetailsFields,
    ProjectFormValues,
    ProjectInstructionsFields,
    ProjectKnowledgeFields,
    toInstructionInputs,
    toKnowledgeSourceInputs,
} from './ProjectFormFields.tsx';
import { ProjectSharingFields } from './ProjectSharingFields.tsx';
import { splitRecipients } from './RecipientsField.tsx';

type Step = 'details' | 'knowledge' | 'sharing';

const STEPS: { key: Step; isValid?: (values: ProjectFormValues) => boolean }[] = [
    { key: 'details', isValid: (values) => values.name.trim().length > 0 },
    {
        key: 'knowledge',
        isValid: (values) => (values.knowledgeUrls || []).every((url) => !url.trim() || isValidKnowledgeUrl(url)),
    },
    { key: 'sharing' },
];

// the same bar as the assistant wizard (CreateAssistantWizard); becomes one shared component once ENG-11231 lands
const ProgressBar = ({ value }: { value: number }) => (
    <div
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={Math.round(value * 100)}
        className="h-1 w-full bg-surface-strong"
    >
        <div
            className="h-full bg-surface-brand-strong-default transition-[width] duration-200"
            style={{ width: `${value * 100}%` }}
        />
    </div>
);

/** Creation follows the hub wizard: details, knowledge, sharing behind a progress bar. The project is created at the
 *  end, its files are uploaded one by one and sharing is applied last. */
export const ProjectCreateModal = ({
    onClose,
    onCreated,
}: {
    onClose: () => void;
    onCreated: (project: any) => void;
}) => {
    const { t } = useTranslation('chat');
    const { publishToast } = useToaster();
    const { user } = useApplicationContext();
    // the creator owns the project; SharePoint and SFTP additionally need the agents or integrations permission
    const canConfigureSources = canConfigureKnowledgeSources(user);

    const form = useForm<ProjectFormValues>({ defaultValues: PROJECT_FORM_DEFAULTS, mode: 'onTouched' });
    const [stepIndex, setStepIndex] = useState(0);
    const [submitting, setSubmitting] = useState(false);

    const [createProject] = useMutation(CREATE_PROJECT);
    const [uploadFile] = useMutation(UPLOAD_PROJECT_KNOWLEDGE_FILE);
    const [shareProject] = useMutation(SHARE_PROJECT);

    const step = STEPS[stepIndex];
    const isLast = stepIndex === STEPS.length - 1;
    const values = form.watch();
    const stepValid = step.isValid ? step.isValid(values) : true;

    const goNext = () => setStepIndex((index) => Math.min(index + 1, STEPS.length - 1));
    const goBack = () => setStepIndex((index) => Math.max(index - 1, 0));
    const close = () => {
        if (!submitting) onClose();
    };

    const handleSubmit = async (data: ProjectFormValues) => {
        // Enter inside a field submits the form; before the last step that only moves on
        if (!isLast) {
            if (stepValid) goNext();
            return;
        }
        setSubmitting(true);
        try {
            const { data: result } = await createProject({
                variables: {
                    name: data.name.trim(),
                    description: data.description.trim() || null,
                    instructions: toInstructionInputs(data.instructions),
                    ...toKnowledgeSourceInputs(data, { includeSynced: canConfigureSources }),
                },
            });
            const project = result.createProject.project;
            for (const file of data.files) {
                try {
                    await uploadFile({ variables: { projectId: project.id, file } });
                } catch (e) {
                    publishToast({
                        description: graphqlErrorMessage(e, t('projects.knowledge.uploadError', { name: file.name })),
                        status: 'error',
                    });
                }
            }
            // the project exists from here on: a failed share must not leave a Create button that makes a duplicate
            let shared = true;
            const { teamId, memberIds } = splitRecipients(data.recipients);
            if (teamId || memberIds.length > 0) {
                try {
                    await shareProject({ variables: { id: project.id, teamId, memberIds } });
                } catch (e) {
                    shared = false;
                    publishToast({
                        description: graphqlErrorMessage(e, t('projects.create.shareFailed', { name: project.name })),
                        status: 'warning',
                    });
                }
            }
            if (shared) {
                publishToast({ description: t('projects.create.done', { name: project.name }), status: 'success' });
            }
            onCreated(project);
        } catch (e) {
            publishToast({ description: graphqlErrorMessage(e, t('projects.saveError')), status: 'error' });
            setSubmitting(false);
        }
    };

    return (
        <Modal
            visible
            onClose={close}
            title={t('projects.create.title')}
            className="w-full sm:w-144 lg:w-176 2xl:w-208 4xl:w-240"
            innerClassName="p-0"
            preventCloseOnOverlayClick
        >
            <div className="flex flex-col">
                <ProgressBar value={(stepIndex + 1) / STEPS.length} />

                <form onSubmit={form.handleSubmit(handleSubmit)} className="flex flex-col gap-6 px-6 pb-6 pt-5">
                    <div className="flex flex-col gap-1">
                        <h3 className="m-0 text-base font-semibold text-content-strong">
                            {t(`projects.create.${step.key}.title`)}
                        </h3>
                        <p className="m-0 text-sm text-content-medium">
                            {t(`projects.create.${step.key}.description`)}
                        </p>
                    </div>

                    <div className="flex flex-col gap-4">
                        {step.key === 'details' && <ProjectDetailsFields form={form} />}
                        {step.key === 'knowledge' && (
                            <>
                                <ProjectKnowledgeFields
                                    form={form}
                                    canEditUrls
                                    canConfigureSources={canConfigureSources}
                                />
                                <ProjectInstructionsFields form={form} />
                            </>
                        )}
                        {step.key === 'sharing' && (
                            <ProjectSharingFields
                                value={values.recipients}
                                onValueChange={(recipients) => form.setValue('recipients', recipients)}
                            />
                        )}
                    </div>

                    <div className="flex items-center justify-between gap-2">
                        <div>
                            {stepIndex > 0 && (
                                <Button type="button" onClick={goBack} disabled={submitting}>
                                    {t('projects.create.back')}
                                </Button>
                            )}
                        </div>
                        <div className="flex gap-2">
                            <Button type="button" onClick={close} disabled={submitting}>
                                {t('projects.create.cancel')}
                            </Button>
                            {/* distinct keys: reusing one DOM node would flip it to type="submit" mid-click and submit */}
                            {isLast ? (
                                <Button
                                    key="create"
                                    type="submit"
                                    variant="primary"
                                    disabled={!stepValid}
                                    loading={submitting}
                                >
                                    {t('projects.create.submit')}
                                </Button>
                            ) : (
                                <Button
                                    key="continue"
                                    type="button"
                                    variant="primary"
                                    onClick={goNext}
                                    disabled={!stepValid}
                                >
                                    {t('projects.create.next')}
                                </Button>
                            )}
                        </div>
                    </div>
                </form>
            </div>
        </Modal>
    );
};
